import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Fetch from '@/utils/Fetch';
import formService from '@/services/form/form-service';
import { FormHeader, FormDefinitionField, FormItem } from '@/interfaces/api/models';
import { getBlankFormItemFromField } from './form-helpers';

export function useFormDefinitionData({ formHeader, isNew }: { formHeader: FormHeader; isNew: boolean }) {
    const fieldsQuery = useQuery(
        ['form-definition-fields', formHeader.FormDefinitionID],
        async () => {
            const response = await Fetch.get({
                url: '/FormDefinition/GetFormDefinitionFields',
                params: { formDefinitionID: formHeader.FormDefinitionID, includeInactive: true },
            });
            let fields: FormDefinitionField[] = response.Results;

            if (!isNew && formHeader.FormItems) {
                formHeader.FormItems.forEach((item) => {
                    const field = fields.find((x) => x.ID === item.FormDefinitionFieldID);
                    if (item.IsActive && field && !field.IsActive) field.IsActive = true;
                });
            }

            return fields.filter((x) => x.IsActive);
        },
        { enabled: !!formHeader.FormDefinitionID },
    );

    const optionsQuery = useQuery(
        ['form-data-options', formHeader.FormDefinition?.MasterID],
        () => formService.getFormDataOptions({ formDefinitionMasterID: formHeader.FormDefinition?.MasterID }),
        { enabled: !!formHeader.FormDefinition?.MasterID },
    );

    const formDefinitionFields = fieldsQuery.data ?? [];
    const formDataOptions = optionsQuery.data?.Results ?? [];
    const isLoading = fieldsQuery.isInitialLoading || optionsQuery.isInitialLoading;

    const initialFormItemsMeta: FormItem[] = useMemo(() => {
        if (isLoading) return [];
        if (isNew) {
            return formDefinitionFields.map((field) => getBlankFormItemFromField(field));
        }
        return formHeader.FormItems ?? [];
    }, [isLoading, isNew, formDefinitionFields, formHeader.FormItems]);

    return { formDefinitionFields, formDataOptions, isLoading, initialFormItemsMeta };
}
